import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getDashboardStats, getUpcomingNodes, getCases } from '../lib/api'
import type { Notification, Case } from '../types'

export default function Dashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [stats, setStats] = useState<any>(null)
  const [upcomingNodes, setUpcomingNodes] = useState<Notification[]>([])
  const [recentCases, setRecentCases] = useState<Case[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [year])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // 并行加载数据，单个失败不影响整体
      const [statsData, nodesData, casesData] = await Promise.all([
        getDashboardStats(year).catch((err: any) => {
          console.error('加载统计数据失败:', err)
          return { closed: 0, active: 0, pending: 0, totalClaimAmount: 0, byType: {}, byRole: {}, totalPayable: 0, totalReceived: 0, pendingAmount: 0 }
        }),
        getUpcomingNodes(7).catch((err: any) => {
          console.error('加载节点失败:', err)
          return []
        }),
        getCases().catch((err: any) => {
          console.error('加载案件失败:', err)
          return []
        }),
      ])
      
      setStats(statsData)
      setUpcomingNodes(nodesData || [])
      setRecentCases((casesData || []).slice(0, 5))
    } catch (err) {
      console.error('加载仪表盘数据失败:', err)
      setError('加载数据失败，请刷新重试')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>
  }

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      pending: '待发起',
      active: '进行中',
      closed: '已结案',
      suspended: '中止',
    }
    return map[status] || status
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">仪表盘</h1>
          <p className="text-gray-500 mt-1">案件概览与到期提醒</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={e => setYear(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={new Date().getFullYear()}>{new Date().getFullYear()} (当前)</option>
            <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
            <option value={new Date().getFullYear() - 2}>{new Date().getFullYear() - 2}</option>
            <option value="all">全部</option>
          </select>
          <button
            onClick={() => navigate('/cases/new')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
          >
            + 新建案件
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="text-sm text-red-600 font-medium mb-2">已结案诉讼</div>
          <div className="text-3xl font-bold text-red-800">{stats?.closed || 0}</div>
          <div className="text-xs text-red-600 mt-1">已完成案件</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="text-sm text-yellow-600 font-medium mb-2">未结案诉讼</div>
          <div className="text-3xl font-bold text-yellow-800">{stats?.active || 0}</div>
          <div className="text-xs text-yellow-600 mt-1">{upcomingNodes.length}个节点即将到期</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="text-sm text-blue-600 font-medium mb-2">待发起诉讼</div>
          <div className="text-3xl font-bold text-blue-800">{stats?.pending || 0}</div>
          <div className="text-xs text-blue-600 mt-1">准备中</div>
        </div>
      </div>

      {/* Payment & Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Overview */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">收款概览</h3>
          <div className="flex gap-8 mb-6">
            <div>
              <div className="text-xs text-gray-500">待收款</div>
              <div className="text-2xl font-bold text-red-600">
                ¥{(stats?.pendingAmount || 0 / 10000).toFixed(0)}万
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">已收款</div>
              <div className="text-2xl font-bold text-green-600">
                ¥{(stats?.totalReceived || 0 / 10000).toFixed(0)}万
              </div>
            </div>
          </div>
        </div>

        {/* Case Distribution */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-4">案件状态分布</h3>
          <div className="space-y-3">
            {[
              { label: '进行中', value: stats?.active || 0, color: 'bg-yellow-500' },
              { label: '已结案', value: stats?.closed || 0, color: 'bg-green-500' },
              { label: '待发起', value: stats?.pending || 0, color: 'bg-gray-400' },
            ].filter(item => item.value > 0).map(item => (
              <div key={item.label} className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${item.color}`} />
                <span className="text-sm text-gray-700 flex-1">{item.label}</span>
                <span className="text-sm font-medium">{item.value}</span>
              </div>
            ))}
            {Object.keys(stats?.byType || {}).length === 0 && (
              <div className="text-center py-8 text-gray-400">暂无案件数据</div>
            )}
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Deadlines */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">即将到期节点</h3>
            <span className="text-xs text-gray-500">共 {upcomingNodes.length} 条</span>
          </div>
          {upcomingNodes.length === 0 ? (
            <div className="text-center py-8 text-gray-400">暂无即将到期节点</div>
          ) : (
            <div className="space-y-3">
              {upcomingNodes.map((node) => (
                <div
                  key={node.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    node.priority === 'critical' ? 'bg-red-50 border-red-200' :
                    node.priority === 'high' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <span className="text-lg">
                    {node.priority === 'critical' ? '🔴' : node.priority === 'high' ? '🟠' : '🟡'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {node.case_id && (
                        <button
                          onClick={() => navigate(`/cases/${node.case_id}`)}
                          className="hover:text-indigo-600 underline"
                        >
                          {node.body?.split('—')[0]?.trim() || node.body}
                        </button>
                      )}
                      {node.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{node.body}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Cases */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">最近案件</h3>
            <button
              onClick={() => navigate('/cases')}
              className="text-xs text-indigo-600 hover:text-indigo-700"
            >
              查看全部
            </button>
          </div>
          {recentCases.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              暂无案件数据
              <button
                onClick={() => navigate('/cases/new')}
                className="mt-2 text-indigo-600 hover:underline"
              >
                点击新建第一个案件
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {recentCases.map(c => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/cases/${c.id}`)}
                  className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{c.case_number || '未立案'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      c.status === 'active' ? 'bg-green-100 text-green-700' :
                      c.status === 'pending' ? 'bg-gray-100 text-gray-600' :
                      c.status === 'closed' ? 'bg-blue-100 text-blue-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {statusLabel(c.status)}
                    </span>
                  </div>
                  <div className="text-sm font-medium mt-1">{c.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{c.opposing_party}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
